import { DownloadSimple, Trash } from '@phosphor-icons/react';
import type { Location } from '@shh/shared';
import { Button, Text, TextField } from '@shh/ui';
import { useState } from 'react';
import { PlaceField } from '../../components/PlaceField';
import { deleteHousehold, downloadExport, friendlyError, updateHousehold } from '../../data/api';
import { ChangeEmailButton, hasPassword } from '../../components/ChangeEmailSheet';
import { FamilyDeleteAccountButton } from '../../components/DeleteAccountSheet';
import { firebase } from '../../lib/firebase';
import { useFamily } from '../../data/family';
import { accountText } from '../../i18n/account';
import { testFamilyText } from '../../i18n/admin';
import { settingsText } from '../../i18n/settings';

/**
 * Settings → Familie, top: family name and town (weather). "Zuhause" was a section of its own
 * until 1.17 – Mike, 11.10.: "Zuhause mit Familie zusammenführen".
 */
export function HomeDetails() {
  const f = useFamily();
  const [name, setName] = useState(f.household?.name ?? '');
  const [place, setPlace] = useState<Location | undefined>(f.household?.location);
  const [saved, setSaved] = useState(false);
  return (
    <form
      className="flex flex-col gap-gap-lg"
      onSubmit={(e) => {
        e.preventDefault();
        void updateHousehold(f.householdId, {
          name: name.trim(),
          ...(place ? { location: place } : {}),
        }).then(() => setSaved(true));
      }}
    >
      {f.household?.plan === 'testfamily' && (
        <Text size="sm" weight="semibold" className="text-accent-secondary">
          {testFamilyText.member}
        </Text>
      )}
      <TextField
        label={settingsText.familyName}
        value={name}
        onChange={(v) => {
          setName(v);
          setSaved(false);
        }}
      />
      <PlaceField
        value={place}
        onChange={(l) => {
          setPlace(l);
          setSaved(false);
        }}
      />
      <div className="flex items-center gap-gap-md">
        <Button type="submit" variant="secondary" isDisabled={!name.trim()}>
          {settingsText.save}
        </Button>
        {saved && (
          <span role="status" className="text-sm font-semibold text-feedback-success">
            {settingsText.saved}
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * Settings → Familie, bottom: everything about your data in one place – download, delete your
 * own account, delete the family (Mike, 11.10.: "die Punkte alle zusammen").
 * Only on a parent's own device: the wall display must never wipe the family.
 */
export function FamilyDataSettings() {
  const f = useFamily();
  if (f.role !== 'parent') return null;
  return (
    <>
      <Export />
      <OwnAccount />
      <StartOver demo={Boolean(f.household?.demo)} />
    </>
  );
}

function OwnAccount() {
  return (
    <section
      aria-labelledby="own-account"
      className="flex flex-col items-start gap-gap-sm border-t border-border-subtle pt-gap-lg"
    >
      <h3 id="own-account" className="font-display text-lg font-black">
        {accountText.sectionTitle}
      </h3>
      <Text size="sm" tone="secondary">
        {accountText.signedInAs(firebase().auth.currentUser?.email ?? '')}
        {!hasPassword() && <> · {accountText.googleManaged}</>}
      </Text>
      <div className="flex flex-wrap gap-gap-sm">
        {hasPassword() && <ChangeEmailButton />}
        <FamilyDeleteAccountButton />
      </div>
    </section>
  );
}

/** DSGVO: "Welche Daten habt ihr von uns?" – one tap, one file. */
function Export() {
  const f = useFamily();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <section
      aria-labelledby="export"
      className="flex flex-col items-start gap-gap-sm border-t border-border-subtle pt-gap-lg"
    >
      <h3 id="export" className="font-display text-lg font-black">
        {settingsText.yourData}
      </h3>
      <Text size="sm" tone="secondary">
        {settingsText.dataText}
      </Text>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-gap-md">
        <Button
          variant="secondary"
          icon={DownloadSimple}
          isDisabled={busy}
          onPress={() => {
            setBusy(true);
            setDone(false);
            setError(undefined);
            downloadExport(f.household?.name ?? '', f.today)
              .then(() => setDone(true))
              .catch((e: unknown) => setError(friendlyError(e)))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? settingsText.collecting : settingsText.download}
        </Button>
        {done && (
          <span role="status" className="text-sm font-semibold text-feedback-success">
            {settingsText.downloaded}
          </span>
        )}
      </div>
    </section>
  );
}

function StartOver({ demo }: { demo: boolean }) {
  const f = useFamily();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const what = demo ? settingsText.demoFamily : settingsText.family;
  return (
    <section
      aria-labelledby="start-over"
      className="flex flex-col items-start gap-gap-sm border-t border-border-subtle pt-gap-lg"
    >
      <h3 id="start-over" className="font-display text-lg font-black">
        {settingsText.startOver}
      </h3>
      <Text size="sm" tone="secondary">
        {demo ? settingsText.demoDelete : settingsText.familyDelete}
      </Text>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      {confirm ? (
        <div className="flex flex-wrap gap-gap-sm">
          <Button
            variant="danger"
            icon={Trash}
            isDisabled={busy}
            onPress={() => {
              setBusy(true);
              setError(undefined);
              // on success the session switches to the setup wizard – nothing more to do here
              void deleteHousehold(f.householdId).catch(() => {
                setError(settingsText.failed);
                setBusy(false);
              });
            }}
          >
            {settingsText.reallyDelete(what)}
          </Button>
          <Button variant="quiet" isDisabled={busy} onPress={() => setConfirm(false)}>
            {settingsText.cancel}
          </Button>
        </div>
      ) : (
        <Button variant="quiet" icon={Trash} onPress={() => setConfirm(true)}>
          {settingsText.delete(what)}
        </Button>
      )}
    </section>
  );
}
